import type { ReactNode } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
} from 'react-native-reanimated';
import { useScrollState } from './ScrollContext';

interface ParallaxProps {
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  /**
   * Parallax rate. Positive lags behind the scroll (drifts down), negative
   * leads it (drifts up). ~0.1–0.3 for backgrounds, smaller for foreground.
   */
  speed?: number;
  /** Disable touch capture (decorative background layers). */
  decorative?: boolean;
}

/**
 * Translates its content vertically as a fraction of the scroll offset to create
 * depth. UI-thread; no-op without a scroll context or under reduced motion.
 */
export const Parallax = ({ children, style, speed = 0.15, decorative }: ParallaxProps) => {
  const scroll = useScrollState();
  const reducedMotion = useReducedMotion();
  const fallback = useSharedValue(0);
  const offset = scroll?.offset ?? fallback;
  const enabled = !!scroll && !reducedMotion;

  const animatedStyle = useAnimatedStyle(() => {
    if (!enabled) return {};
    return { transform: [{ translateY: offset.value * speed }] };
  });

  return (
    <Animated.View style={[style, decorative ? styles.decorative : null, animatedStyle]}>
      {children}
    </Animated.View>
  );
};

const styles = {
  decorative: { pointerEvents: 'none' as const },
};
